import { type JSONContent } from '@tiptap/react';
import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from 'react';

import type { ArticleSettings } from '@xing-com/crate-common-graphql-types';
import {
  ArticleMembership,
  ArticlePublicationState,
} from '@xing-com/crate-common-graphql-types';

import { type ArticleType } from '../types';

export const MAX_HEADLINE_CHARACTERS = 360;
export const MAX_SUBTITLE_CHARACTERS = 360;

const defaultArticle: ArticleType = {
  id: '',
  globalId: '',
  slug: '',
  headerImageURL: '',
  headerImageCaption: '',
  headline: '',
  subtitle: '',
  content: {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
      },
    ],
  },
  publishedAt: null,
  publicationState: ArticlePublicationState.Draft,
  settings: {
    noindex: false,
    titleTag: null,
    descriptionTag: null,
    membership: ArticleMembership.NoMembership,
  },
};

export type ArticleEditorState = {
  article: ArticleType;
  updateArticleIds(globalId: string, id: string): void;
  updateHeaderImage(
    articleGlobalId: string,
    headerImageURL: string,
    headerImageCaption?: string
  ): void;
  updateHeaderVideo(
    articleGlobalId: string,
    headerVideoRef: string,
    headerVideoCaption?: string
  ): void;
  updateHeadline(headline: string): void;
  updateSubtitle(subtitle: string): void;
  updateContent(content: JSONContent): void;
  updateArticle(newArticle: ArticleType): void;
  updatePublishedAt(publishDate: string | null): void;
  updateSettings(settings: ArticleSettings): void;
  resetArticle(): void;
};

const ArticleEditorContext = createContext<ArticleEditorState | undefined>(
  undefined
);

const ArticleEditorProvider: React.FC<
  { article?: ArticleType | null } & PropsWithChildren
> = ({ article: propArticle, children }) => {
  const [article, setArticle] = useState<ArticleType>(
    propArticle ? { ...propArticle } : { ...defaultArticle }
  );

  const updateHeaderImage = (
    articleGlobalId: string,
    headerImageURL: string,
    headerImageCaption = ''
  ): void => {
    setArticle((prev) => ({
      ...prev,
      globalId: articleGlobalId,
      coverType: headerImageURL ? 'image' : undefined,
      headerImageURL,
      headerImageCaption,
    }));
  };

  const updateHeaderVideo = (
    articleGlobalId: string,
    headerVideoRef: string,
    headerVideoCaption = ''
  ): void => {
    setArticle((prev) => ({
      ...prev,
      globalId: articleGlobalId,
      coverType: headerVideoRef ? 'video' : undefined,
      headerVideoRef,
      headerVideoCaption,
    }));
  };

  const updateArticleIds = (globalId: string, id: string): void => {
    setArticle((prev) => ({ ...prev, globalId, id }));
  };

  const updateHeadline = (headline: string): void => {
    if (headline.length > MAX_HEADLINE_CHARACTERS) return;
    setArticle((prev) => ({ ...prev, headline }));
  };

  const updateSubtitle = (subtitle: string): void => {
    if (subtitle.length > MAX_SUBTITLE_CHARACTERS) return;
    setArticle((prev) => ({ ...prev, subtitle }));
  };

  const updateContent = (content: JSONContent): void => {
    setArticle((prev) => ({ ...prev, content }));
  };

  const updateArticle = (newArticle: ArticleType): void => {
    setArticle({ ...newArticle });
  };

  const updatePublishedAt = (publishDate: string | null): void => {
    setArticle((prev) => ({ ...prev, publishedAt: publishDate }));
  };

  const updateSettings = (settings: ArticleSettings): void => {
    setArticle((prev) => ({ ...prev, settings }));
  };

  const resetArticle = (): void => {
    setArticle({ ...defaultArticle });
  };

  const value = {
    article,
    updateArticleIds,
    updateHeaderImage,
    updateHeaderVideo,
    updateHeadline,
    updateSubtitle,
    updateContent,
    updateArticle,
    updateSettings,
    resetArticle,
    updatePublishedAt,
  };
  return (
    <ArticleEditorContext.Provider value={value}>
      {children}
    </ArticleEditorContext.Provider>
  );
};

const useArticleEditor = (): ArticleEditorState => {
  const context = useContext(ArticleEditorContext);

  if (context === undefined)
    throw new Error('useArticle should only be used within ArticleProvider');

  return context;
};

export { ArticleEditorProvider, useArticleEditor };
